/**
 * CosmologyMap — pixel-accurate world zone data derived from cosmology.png.
 *
 * The cosmology sprite (73×111 pixels) is the authoritative reference for world
 * shape and biome placement. Each pixel maps to a 40×40 game-tile area. The map
 * center (pixel 36,57) corresponds to the grass surface row at the World Tree.
 *
 * The tile grid is vertically aligned so the BOTTOM edge of pixel 57
 * (GRASS_SURFACE) sits exactly at UNDERGROUND_LEVEL (y=13). This means:
 *   - Pixel 57 (GRASS_SURFACE) spans tiles y=-27..12 (contains the surface at y≈4)
 *   - Pixel 58 (STONE) spans tiles y=13..52 (starts at UNDERGROUND_LEVEL)
 *   - Dungeon tower bottom pixels (py=57) sit above the underground transition
 *   - Stone under each dungeon starts exactly at y=13
 *
 * The top-left tile of pixel 57 is at TILE_GRID_ORIGIN_Y = UNDERGROUND_LEVEL - PIXELS_TO_TILES = -27.
 *
 * Coordinate conversion:
 *   pixelX → tileX:  (pixelX - CENTER_PX) * PIXELS_TO_TILES
 *   pixelY → tileY:  (pixelY - CENTER_PY) * PIXELS_TO_TILES + TILE_GRID_ORIGIN_Y
 *   tileX  → pixelX: Math.floor(tileX / PIXELS_TO_TILES) + CENTER_PX
 *   tileY  → pixelY: Math.floor((tileY - TILE_GRID_ORIGIN_Y) / PIXELS_TO_TILES) + CENTER_PY
 */

import { TileType, UNDERGROUND_LEVEL } from './constants';

// ── Zone Enum ──────────────────────────────────────────────────────────

export enum CosmologyZone {
    GOD              = 0,   // #ffe602 — One True God at the apex
    MOTHER_WATER     = 1,   // #253495 — Primordial feminine ocean
    OUROBOROS        = 2,   // #ff00ff — Boundary serpent, world edge
    SKY              = 3,   // #0af6f8 — Firmament dome
    ICE_WALL         = 4,   // #00ffe1 — Ice wall inside ouroboros
    OCEAN            = 5,   // #596cec — Surrounding sea
    GIANT_DRAGON     = 6,   // #0000ff — Eastern colossus (wood-air, spring)
    RIVER_DRAGON     = 7,   // #313175 — Magenta River (metamorphosis)
    GIANT_PHOENIX    = 8,   // #83ffb5 — Southern colossus (fire, summer)
    RIVER_PHOENIX    = 9,   // #f7f5c0 — Red River (blood, fire, judgment)
    GIANT_LION       = 10,  // #000000 — Western colossus (earth-metal, autumn)
    RIVER_LION       = 11,  // #ff0000 — Black River (death, iron, gravity)
    GIANT_TURTLE     = 12,  // #aaaaaa — Northern colossus (water, winter)
    RIVER_TURTLE     = 13,  // #9602ff — White River (forgetfulness)
    YGGDRASIL_LEAVES = 14,  // #40c440 — World Tree canopy
    YGGDRASIL_TRUNK  = 15,  // #808000 — World Tree trunk and root system
    GRASS_SURFACE    = 16,  // #00ff00 — Earth surface / ground level
    DUNGEON_TOWER    = 17,  // #808080 — Dungeon towers on surface
    STONE            = 18,  // #a7a7a7 — Stone underground (default subsurface)
    HELL_1_THRESHOLD = 19,  // #804000 — L1: Entry, guardian, toll
    HELL_2_JUDGMENT  = 20,  // #a79f60 — L2: Sorting, weighing, crossroads
    HELL_3_WANDERING = 21,  // #97b941 — L3: Grey wandering, diminished echo
    HELL_4_TRIALS    = 22,  // #67914a — L4: Houses, gauntlets, tests
    HELL_5_PUNISHMENT= 23,  // #308641 — L5: Active torment, proportional
    HELL_6_PRISON    = 24,  // #81aea6 — L6: Containment of cosmic threats
    TARTARUS         = 25,  // #5c195c — Inverted pyramid nadir
    VOID             = 26,  // #3f4f31 — Infinite void outside all
}

// ── Map Constants ──────────────────────────────────────────────────────

/** Width of cosmology sprite in pixels. */
export const COSMOLOGY_WIDTH = 73;

/** Height of cosmology sprite in pixels. */
export const COSMOLOGY_HEIGHT = 111;

/** Tiles per cosmology pixel (each pixel = 40×40 tile area). */
export const PIXELS_TO_TILES = 40;

/** Pixel X of world center (God's column, World Tree, spawn). */
export const CENTER_PX = 36;

/** Pixel Y of grass surface row. */
export const CENTER_PY = 57;

/**
 * Tile Y at the top-left of the CENTER_PY pixel row.
 * The bottom edge of this pixel (= top of pixel 58) aligns with UNDERGROUND_LEVEL,
 * so its top edge is one full pixel (40 tiles) higher.
 */
export const TILE_GRID_ORIGIN_Y = UNDERGROUND_LEVEL - PIXELS_TO_TILES;  // = -27

// ── Zone Colors (hex) ──────────────────────────────────────────────────

export const COSMOLOGY_ZONE_COLORS: Record<CosmologyZone, number> = {
    [CosmologyZone.GOD]:              0xffe602,
    [CosmologyZone.MOTHER_WATER]:     0x253495,
    [CosmologyZone.OUROBOROS]:        0xff00ff,
    [CosmologyZone.SKY]:              0x0af6f8,
    [CosmologyZone.ICE_WALL]:         0x00ffe1,
    [CosmologyZone.OCEAN]:            0x596cec,
    [CosmologyZone.GIANT_DRAGON]:     0x0000ff,
    [CosmologyZone.RIVER_DRAGON]:     0x313175,
    [CosmologyZone.GIANT_PHOENIX]:    0x83ffb5,
    [CosmologyZone.RIVER_PHOENIX]:    0xf7f5c0,
    [CosmologyZone.GIANT_LION]:       0x000000,
    [CosmologyZone.RIVER_LION]:       0xff0000,
    [CosmologyZone.GIANT_TURTLE]:     0xaaaaaa,
    [CosmologyZone.RIVER_TURTLE]:     0x9602ff,
    [CosmologyZone.YGGDRASIL_LEAVES]: 0x40c440,
    [CosmologyZone.YGGDRASIL_TRUNK]:  0x808000,
    [CosmologyZone.GRASS_SURFACE]:    0x00ff00,
    [CosmologyZone.DUNGEON_TOWER]:    0x808080,
    [CosmologyZone.STONE]:            0xa7a7a7,
    [CosmologyZone.HELL_1_THRESHOLD]: 0x804000,
    [CosmologyZone.HELL_2_JUDGMENT]:  0xa79f60,
    [CosmologyZone.HELL_3_WANDERING]: 0x97b941,
    [CosmologyZone.HELL_4_TRIALS]:    0x67914a,
    [CosmologyZone.HELL_5_PUNISHMENT]:0x308641,
    [CosmologyZone.HELL_6_PRISON]:    0x81aea6,
    [CosmologyZone.TARTARUS]:         0x5c195c,
    [CosmologyZone.VOID]:             0x3f4f31,
};

// ── Pixel Data ─────────────────────────────────────────────────────────
// 73×111 = 8,103 zone values encoded as base64. Each byte is a CosmologyZone.

const COSMOLOGY_DATA_B64 =
'GhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaG' +
'hoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGh' +
'oaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoAGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGho' +
'aGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoAAAAaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoa' +
'GhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoAAAAAABoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaG' +
'hoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGh' +
'oaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGho' +
'aGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBARoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoa' +
'GhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaG' +
'hoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGh' +
'oaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBARoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGho' +
'aGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoa' +
'GhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEaGhoaGhoaGhoaGhoaGhoaGhoaGhoaG' +
'hoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBARoaGhoaGhoaGhoaGhoaGhoaGhoaGh' +
'oaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBGhoaGhoaGhoaGhoaGhoaGhoaGho' +
'aGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEaGhoaGhoaGhoaGhoaGhoaGhoa' +
'GhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBARoaGhoaGhoaGhoaGhoaGhoaG' +
'hoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBGhoaGhoaGhoaGhoaGhoaGh' +
'oaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEaGhoaGhoaGhoaGhoaGho' +
'aGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBARoaGhoaGhoaGhoaGhoa' +
'GhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBGhoaGhoaGhoaGhoaG' +
'hoaGhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEaGhoaGhoaGhoaGh' +
'oaGhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBARoaGhoaGhoaGho' +
'aGhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBGhoaGhoaGhoa' +
'GhoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEaGhoaGhoaG' +
'hoaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBARoaGhoaGh' +
'oaGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBGhoaGho' +
'aGhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEaGhoa' +
'GhoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBARoaG' +
'hoaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBGh' +
'oaGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQE' +
'aGhoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEB' +
'ARoaGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBA' +
'QEBGhoaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQ' +
'EBAQEaGhoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQE' +
'BAQEBARoaGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEB' +
'AQEBAQEBGhoaGhoBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBA' +
'QEBAQEBAQEaGhoaAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAg' +
'ICAgICAgICGhoaGgIDAwMDAwMDAwYGAwMDAwMGBgMDCAMDCAMDCAMDAwMDAwMDAwMDAwMDCgoDCgMKAwoKAwwMAwMDAwMMDAM' +
'DAwMDAwMDAhoaGhoCAwMDAwMDAwMGBgMDAwMDBgYDCAgDAwgDAwgIAwMDAwMODg4DAwMDAwoDAwoDCgMDCgMMDAMDDAMDDAwD' +
'AwMDAwMDAwIaGhoaAgMDAwMDAwMDBgYDBgYGAwYGAwgIAwgICAMICAMDAwMODg4ODgMDAwMKCgMKCgoDCgoDDAwDDAwMAwwMA' +
'wMDAwMDAwMCGhoaGgIDAwMDAwMDAwYGAwYGBgMGBgMICAMICAgDCAgDAwMDDg4ODg4DAwMDCgoDCgoKAwoKAwwMAwwMDAMMDA' +
'MDAwMDAwMDAhoaGhoCAwMDAwMDAwMGBgMDBgMDBgYDCAgDAwgDAwgIAwMDAw4ODg4OAwMDAwoKAwMKAwMKCgMMDAMDDAMDDAw' +
'DAwMDAwMDAwIaGhoaAgMDAwMDAwMDBgYGBgYGBgYGAwgICAgICAgICAMDAwMDDg4OAwMDAwMKCgoKCgoKCgoDDAwMDAwMDAwM' +
'AwMDAwMDAwMCGhoaGgIDAwMDAwMDAwYGBgYGBgYGBgMICAgDCAMICAgDAwMDAwMPAwMDAwMDCgoKCgoKCgoKAwMDDAwMDAwDA' +
'wMDAwMDAwMDAhoaGhoCAwMDAwMDAwMDAwYGBgYGAwMDAwMDAwgDAwMDAwMDAwMDDwMDAwMDAwMDCgoKCgoDAwMDAwwMDAwMAw' +
'MDAwMDAwMDAwIaGhoaAgMDAwMDAwMDAwMDBgYGAwMDAwMDAwMIAwMDDg4OAwMDAw8DAwMDDg4OAwMKCgoDAwMDAwwMDAwMDAw' +
'DAwMDAwMDAwMCGhoaGgIDAwMDAwMDAwMDAwYGBgMDAwMDAwMICAgDDg4ODg4DAwMPAwMDDg4ODg4DCgoKAwMDAwMMDAwMDAwM' +
'AwMDAwMDAwMDAhoaGhoCAwMDAwMDAwMDAwMGBgYDAwMDAwMDCAgIAw4ODg4ODw8PDw8PDw4ODg4OAwMKAwMDAwMDDAwMDAwMD' +
'AMDAwMDAwMDAwIaGhoaAgQDAwMDAwMDAwMDBgYGAwMDAwMDAwgICAMODg4ODgMDAw8DAwMODg4ODgMDCgMDAwMDAwMMDAwMDA' +
'MDAwMDAwMDAwQCGhoaGgIEAwMDAwMDAwMDBgYGBgYDAwMDAwMICAgDAw4ODgMDAwMPAwMDAw4ODgMDCgoKAwMDAwMDAwwMDAM' +
'DAwMDAwMDAwMEAhoaGhoCBAMDAwMDAwMDAwYGAwYGAwMDAwMDCAgIAwMDAwMDAwMDDwMDAwMDAwMDCgoKCgoDAwMDAwMMDAwD' +
'AwMDAwMDAwMDBAIaGhoaAgQDAwMDAwMDAwMGBgMGBgMDAwMDAwgICAMDAwMDAwMDDw8PAwMDAwMDAwoKAwoKAwMDAwMMDAMMD' +
'AMDAwMDAwMDAwQCGhoaGgIEAwMDAwMDAwMDBgYDBgYDAwMDAwMICAgDAwMDAwMDAw8PDwMDAwMDAwMDCgMKAwMDAwMDDAwDDA' +
'wDAwMDAwMDAwMEAhoaGhoCBAMDAwMDAwMDAwYGAwYGAwMDAwMICAgICAMDAwMDAwMPDw8DAwMDAwMDAwoDCgMDAwMDAwwMAww' +
'MAwMDAwMDAwMDBAIaGhoaAgQDAwMDAwMRAwMGBgMGBgMDEQMDCAgICAgDAxEDAwMDDw8PAwMDAxEDAwMKAwoDAwMRAwMMDAMM' +
'DAMDEQMDAwMDAwQCGhoaGgIEAwMDAwMDEQMDBgYDBgYDAxEDAwgICAgIAwMRAwMDAw8PDwMDAwMRAwMDCgMKAwMDEQMDDAwDD' +
'AwDAxEDAwMDAwMEAhoaGhoCBAMDAwMQEBEQEBAQEBAQEBAREBAQEBAQEBAQERAQEBAPDw8QEBAQERAQEBAQEBAQEBEQEBAQEB' +
'AQEBAREBADAwMDBAIaGhoaAgQFBQUFEhISEgcHEhISBwcSEhIJCRISEgkJEhISEhISDw8PEhISEhISCwsSEhILCxISEg0NEhI' +
'SDQ0SEhISBQUFBQQCGhoaGgIEBQUFEhISEhISBwcSBwcSEhISEgkJEgkJEhISEhISEg8PDxISEhISEhILCxILCxISEhISDQ0S' +
'DQ0SEhISEhIFBQUEAhoaGhoCBAUFEhISEhISEhIHBwcSEhISEhISCQkJEhISEhISEhIPDw8SEhISEhISEgsLCxISEhISEhIND' +
'Q0SEhISEhISEgUFBAIaGhoaAgQFEhISEhISEhISEgcSEhISEhISEhIJEhISEhISEhISDw8PEhISEhISEhISCxISEhISEhISEg' +
'0SEhISEhISEhISBQQCGhoaGgIEEhISEhISEhISEhIHEhISEhISEhISCRISEhISEhISEg8PDxISEhISEhISEgsSEhISEhISEhI' +
'NEhISEhISEhISEhIEAhoaGhoCBBISEhISEhISEhISBxISEhISEhISEgkSEhISEhISEhIPDw8SEhISEhISEhILEhISEhISEhIS' +
'DRISEhISEhISEhISBAIaGhoaAgQSEhISEhISEhISEgcSEhISEhIPDw8JDw8PDw8PDw8PDw8PDw8PDw8PDw8PCw8PDxISEhISE' +
'g0SEhISEhISEhISEgQCGhoaGgIEEhISEhISEhISEhIHEhISEhISEhISCRISEg8SEhISEhIPEhISEhISDxISEgsSEhISEhISEh' +
'INEhISEhISEhISEhIEAhoaGhoCBBISEhISEhISEhISBxISEhISEhISEgkSEhIPEhISEhISDxISEhISEg8SEhILEhISEhISEhI' +
'SDRISEhISEhISEhISBAIaGhoaAhISEhISEhISEg8PDwcPDw8PDw8PDw8JDw8PDxISEhISEg8SEhISEhIPDw8PCw8PDw8PDw8P' +
'Dw0PDw8SEhISEhISEhICGhoaGgISEhISEhISEhISEhIHEhISEhISEhISCRIPEhISEhIPDw8PDw8PEhISEhIPEgsSEhISEhISE' +
'hINEhISEhISEhISEhISAhoaGhoCEhISEhISEhISEhISBxISEhISEhISEgkSDxISEhISDxISDxISDxISEhISDxILEhISEhISEh' +
'ISDRISEhISEhISEhISEgIaGhoaAhISEhISEhISEhISEgcSEhISEhIPDw8JDw8SEhISEg8SEg8SEg8SEhISEg8PCw8PDxISEhI' +
'SEg0SEhISEhISEhISEhICGhoaGgISEhISEhISEhISEhIHEhISEhISEhISCRISEhIPDw8PEhIPEhIPDw8PEhISEgsSEhISEhIS' +
'EhINEhISEhISEhISEhISAhoaGhoCEhISEhISEhISEhISBxISEhISEhISEgkSEhISDxISDxISDxISDxISDxISEhILEhISEhISE' +
'hISDRISEhISEhISEhISEgIaGhoaAhISEhISEhISEhISEgcSEhISEhISEhIJEhISEg8SEg8SEg8SEg8SEg8SEhISCxISEhISEh' +
'ISEg0SEhISEhISEhISEhICGhoaGgISEhISEhISEhISEhIHEhISEhISEhISCRISEhIPEhIPEhIPEhIPEhIPEhISEgsSEhISEhI' +
'SEhINEhISEhISEhISEhISAhoaGhoCEhISEhISEhISEhISBxISEhISEhISEgkSEhISDxISEhISDxISEhISDxISEhILEhISEhIS' +
'EhISDRISEhISEhISEhISEgIaGhoaAhISEhISEhISEhISEgcSEhISEhISEhIJEhISEg8SEhISEg8SEhISEg8SEhISCxISEhISE' +
'hISEg0SEhISEhISEhISEhICGhoaGgISEhISEhISExMTExMHExMTExMTExMTCRMTExMTExMTExMTExMTExMTExMTEwsTExMTEx' +
'MTExMNExMTExMSEhISEhISAhoaGhoCEhISEhISEhISEhISBxISEhISEhISEgkSEhISDxISEhISDxISEhISDxISEhILEhISEhI' +
'SEhISDRISEhISEhISEhISEgIaGhoaAhISEhISEhISEhISEgcSEhISEhISEhIJEhISEg8SEg8PDw8PDw8SEg8SEhISCxISEhIS' +
'EhISEg0SEhISEhISEhISEhICGhoaGgISEhISEhISEhISEhIHEhISEhISEhISCRISEhISEhIPEhIPEhIPEhISEhISEgsSEhISE' +
'hISEhINEhISEhISEhISEhISAhoaGhoCEhISEhISEhISEhIUBxQUFBQUFBQUFAkUFBQUFBQUFBQUFBQUFBQUFBQUFBQLFBQUFB' +
'QUFBQUDRQSEhISEhISEhISEgIaGhoaAhISEhISEhISEhISEgcSEhISEhISEhIJEhISEhISEg8SEg8SEg8SEhISEhISCxISEhI' +
'SEhISEg0SEhISEhISEhISEhICGhoaGgISEhISEhISEhISEhIHBxISEhISEhISCRISEhISEhIPEhIPEhIPEhISEhISEgsSEhIS' +
'EhISEg0NEhISEhISEhISEhISAhoaGhoCEhISEhISEhISEhISEgcHEhISEhISEgkSEhISEhISEhISDxISEhISEhISEhILEhISE' +
'hISEg0NEhISEhISEhISEhISEgIaGhoaAhISEhISEhISEhISEhISBwcVFRUVFRUJFRUVFRUVFRUVFRUVFRUVFRUVFRUVCxUVFR' +
'UVFQ0NEhISEhISEhISEhISEhICGhoaGgISEhISEhISEhISEhISEhIHBxISEhISCRISEhISEhISEhIPEhISEhISEhISEgsSEhI' +
'SEg0NEhISEhISEhISEhISEhISAhoaGhoCEhISEhISEhISEhISEhISEgcHEhISEgkSEhISEhISEhISDxISEhISEhISEhILEhIS' +
'Eg0NEhISEhISEhISEhISEhISEgIaGhoaAhISEhISEhISEhISEhISEhISBwcSEhIJEhISEhISEhISEg8SEhISEhISEhISCxISE' +
'g0NEhISEhISEhISEhISEhISEhICGhoaGgISEhISEhISEhISEhISEhISEhIHBxYWCQkWFhYWFhYWFhYWFhYWFhYWFhYWCwsWFg' +
'0NEhISEhISEhISEhISEhISEhISAhoaGhoCEhISEhISEhISEhISEhISEhISEgcHEhIJCRISEhISEhISDxISEhISEhISCwsSEg0' +
'NEhISEhISEhISEhISEhISEhISEgIaGhoaAhISEhISEhISEhISEhISEhISEhISBwcSEgkJEhISEhISEg8SEhISEhISCwsSEg0N' +
'EhISEhISEhISEhISEhISEhISEhICGhoaGgISEhISEhISEhISEhISEhISEhISEhIHBxISCQkSEhISEhISEhISEhISCwsSEg0NE' +
'hISEhISEhISEhISEhISEhISEhISAhoaGhoCEhISEhISEhISEhISEhISEhISEhISEgcHFxcJCRcXFxcXFxcXFxcXCwsXFw0NEh' +
'ISEhISEhISEhISEhISEhISEhISEgIaGhoaAhISEhISEhISEhISEhISEhISEhISEhISBwcSEgkJEhISEhISEhISCwsSEg0NEhI' +
'SEhISEhISEhISEhISEhISEhISEhICGhoaGgISEhISEhISEhISEhISEhISEhISEhISEhIHBxISCQkSEhISEhISCwsSEg0NEhIS' +
'EhISEhISEhISEhISEhISEhISEhISAhoaGhoCEhISEhISEhISEhISEhISEhISEhISEhISEgcHEhIJCRISEhISCwsSEg0NEhISE' +
'hISEhISEhISEhISEhISEhISEhISEgIaGhoaAhISEhISEhISEhISEhISEhISEhISEhISEhISBwcYGAkJGBgYCwsYGA0NEhISEh' +
'ISEhISEhISEhISEhISEhISEhISEhICGhoaGgISEhISEhISEhISEhISEhISEhISEhISEhISEhIHBxISCQkSCwsSEg0NEhISEhI' +
'SEhISEhISEhISEhISEhISEhISEhISAhoaGhoCEhISEhISEhISEhISEhISEhISEhISEhISEhISEgcHEhIJEgsSEg0NEhISEhIS' +
'EhISEhISEhISEhISEhISEhISEhISEgIaGhoaAhISEhISEhISEhISEhISEhISEhISEhISEhISEhISBwcSCRILEg0NEhISEhISE' +
'hISEhISEhISEhISEhISEhISEhISEhICGhoaGgISEhISEhISEhISEhISEhISEhISEhISEhISEhISEhIZGRkZGRkZEhISEhISEh' +
'ISEhISEhISEhISEhISEhISEhISEhISAhoaGhoCEhISEhISEhISEhISEhISEhISEhISEhISEhISEhISEhkZGRkZEhISEhISEhI' +
'SEhISEhISEhISEhISEhISEhISEhISEgIaGhoaAhISEhISEhISEhISEhISEhISEhISEhISEhISEhISEhISGRkZEhISEhISEhIS' +
'EhISEhISEhISEhISEhISEhISEhISEhICGhoaGgISEhISEhISEhISEhISEhISEhISEhISEhISEhISEhISEhIZEhISEhISEhISE' +
'hISEhISEhISEhISEhISEhISEhISEhISAhoaGhoCAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAg' +
'ICAgICAgICAgICAgICAgICAgICAgICAgIaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGho' +
'aGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoa' +
'GhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaG' +
'hoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGh' +
'oaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGho' +
'aGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoa';

// ── Decoded pixel data (lazily initialized) ────────────────────────────

let _zoneData: Uint8Array | null = null;

function decodeZoneData(): Uint8Array {
    if (_zoneData) return _zoneData;
    // Decode base64 → Uint8Array
    const raw = atob(COSMOLOGY_DATA_B64);
    _zoneData = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) {
        _zoneData[i] = raw.charCodeAt(i);
    }
    return _zoneData;
}

// Node.js compatibility: atob may not exist in older Node versions
declare function atob(data: string): string;

// ── Lookup Functions ───────────────────────────────────────────────────

/**
 * Get the cosmology zone at a given pixel coordinate in the sprite.
 * Returns CosmologyZone.VOID for out-of-bounds coordinates.
 */
export function getZoneAtPixel(px: number, py: number): CosmologyZone {
    if (px < 0 || px >= COSMOLOGY_WIDTH || py < 0 || py >= COSMOLOGY_HEIGHT) {
        return CosmologyZone.VOID;
    }
    const data = decodeZoneData();
    return data[py * COSMOLOGY_WIDTH + px] as CosmologyZone;
}

/**
 * Get the cosmology zone at a given game tile coordinate.
 *
 * The tile grid is aligned so the bottom of pixel 57 (GRASS_SURFACE) sits
 * at UNDERGROUND_LEVEL (y=13). Tiles at or below UNDERGROUND_LEVEL that
 * fall in a GRASS_SURFACE pixel are reclassified as STONE so normal
 * terrain generation handles the underground portion.
 */
export function getZoneAtTile(tileX: number, tileY: number): CosmologyZone {
    const px = Math.floor(tileX / PIXELS_TO_TILES) + CENTER_PX;
    const py = Math.floor((tileY - TILE_GRID_ORIGIN_Y) / PIXELS_TO_TILES) + CENTER_PY;
    const zone = getZoneAtPixel(px, py);
    // Align GRASS_SURFACE/STONE boundary with UNDERGROUND_LEVEL
    if (zone === CosmologyZone.GRASS_SURFACE && tileY >= UNDERGROUND_LEVEL) {
        return CosmologyZone.STONE;
    }
    return zone;
}

/**
 * Convert a game tile coordinate to a cosmology pixel coordinate.
 * Y is offset by TILE_GRID_ORIGIN_Y so the bottom of pixel 57 aligns with y=13.
 */
export function tileToPixel(tileX: number, tileY: number): { px: number; py: number } {
    return {
        px: Math.floor(tileX / PIXELS_TO_TILES) + CENTER_PX,
        py: Math.floor((tileY - TILE_GRID_ORIGIN_Y) / PIXELS_TO_TILES) + CENTER_PY,
    };
}

/**
 * Convert a cosmology pixel coordinate to the top-left game tile of that pixel's area.
 * Y includes TILE_GRID_ORIGIN_Y offset so the bottom of pixel 57 aligns with y=13.
 */
export function pixelToTile(px: number, py: number): { tileX: number; tileY: number } {
    return {
        tileX: (px - CENTER_PX) * PIXELS_TO_TILES,
        tileY: (py - CENTER_PY) * PIXELS_TO_TILES + TILE_GRID_ORIGIN_Y,
    };
}

// ── Zone Classification Helpers ────────────────────────────────────────

/** True if the zone is any of the four colossi. */
export function isColossusZone(zone: CosmologyZone): boolean {
    return zone === CosmologyZone.GIANT_DRAGON
        || zone === CosmologyZone.GIANT_PHOENIX
        || zone === CosmologyZone.GIANT_LION
        || zone === CosmologyZone.GIANT_TURTLE;
}

/** True if the zone is any of the four rivers. */
export function isRiverZone(zone: CosmologyZone): boolean {
    return zone === CosmologyZone.RIVER_DRAGON
        || zone === CosmologyZone.RIVER_PHOENIX
        || zone === CosmologyZone.RIVER_LION
        || zone === CosmologyZone.RIVER_TURTLE;
}

/** True if the zone is any underworld layer (hell 1-6 or tartarus). */
export function isUnderworldZone(zone: CosmologyZone): boolean {
    return zone >= CosmologyZone.HELL_1_THRESHOLD && zone <= CosmologyZone.TARTARUS;
}

/** True if the zone is above ground (sky, colossi, yggdrasil canopy). */
export function isSkyZone(zone: CosmologyZone): boolean {
    return zone === CosmologyZone.SKY
        || isColossusZone(zone)
        || zone === CosmologyZone.YGGDRASIL_LEAVES;
}

/** True if the zone is outside the Ouroboros boundary (void, god, mother water). */
export function isOutsideWorld(zone: CosmologyZone): boolean {
    return zone === CosmologyZone.VOID
        || zone === CosmologyZone.GOD
        || zone === CosmologyZone.MOTHER_WATER;
}

/** True if the zone is part of Yggdrasil (World Tree trunk or canopy). */
export function isYggdrasilZone(zone: CosmologyZone): boolean {
    return zone === CosmologyZone.YGGDRASIL_TRUNK
        || zone === CosmologyZone.YGGDRASIL_LEAVES;
}

/**
 * Returns the main fill block for a cosmology zone, or null for zones that use
 * normal terrain generation (sky/grass/stone layers).
 *
 * Cosmic boundaries + World Tree → KASI_ALE (indestructible).
 * Colossi → distinct KIWEN blocks matching each cardinal's Toki Pona word.
 * Water bodies → WATER.
 * Hell layers → distinct KIWEN blocks matching Toki Pona layer names.
 */
export function getZoneMainBlock(zone: CosmologyZone): TileType | null {
    switch (zone) {
        // Cosmic boundaries — indestructible
        case CosmologyZone.GOD:              return TileType.KASI_ALE;
        case CosmologyZone.MOTHER_WATER:     return TileType.KASI_ALE;
        case CosmologyZone.OUROBOROS:         return TileType.KASI_ALE;
        case CosmologyZone.VOID:             return TileType.KASI_ALE;

        // Frozen barrier at world edge
        case CosmologyZone.ICE_WALL:         return TileType.KIWEN_LETE;

        // Water bodies
        case CosmologyZone.OCEAN:            return TileType.WATER;
        case CosmologyZone.RIVER_DRAGON:     return TileType.WATER;
        case CosmologyZone.RIVER_PHOENIX:    return TileType.WATER;
        case CosmologyZone.RIVER_LION:       return TileType.WATER;
        case CosmologyZone.RIVER_TURTLE:     return TileType.WATER;

        // Colossi — cardinal pillars, each block matches its Toki Pona word
        case CosmologyZone.GIANT_DRAGON:     return TileType.KIWEN_ANTE;  // ante = change (wood-air, spring, transformation)
        case CosmologyZone.GIANT_PHOENIX:    return TileType.KIWEN_LOJE;  // loje = red (fire, summer, solar-active)
        case CosmologyZone.GIANT_LION:       return TileType.KIWEN_WAWA;  // wawa = power (earth-metal, autumn, martial strength)
        case CosmologyZone.GIANT_TURTLE:     return TileType.KIWEN_SONA;  // sona = wisdom (water, winter, contemplation)

        // World Tree — indestructible
        case CosmologyZone.YGGDRASIL_LEAVES: return TileType.KASI_ALE;
        case CosmologyZone.YGGDRASIL_TRUNK:  return TileType.KASI_ALE;

        // Dungeon towers — building stone
        case CosmologyZone.DUNGEON_TOWER:    return TileType.RED_STONE;

        // Hell layers — progressively ominous KIWEN blocks
        case CosmologyZone.HELL_1_THRESHOLD: return TileType.RED_STONE;    // entry (matches dungeon towers)
        case CosmologyZone.HELL_2_JUDGMENT:  return TileType.KIWEN_MANI;   // weighing/value
        case CosmologyZone.HELL_3_WANDERING: return TileType.KIWEN_PIMEJA; // grey/dark
        case CosmologyZone.HELL_4_TRIALS:    return TileType.KIWEN_PAKALA; // broken
        case CosmologyZone.HELL_5_PUNISHMENT:return TileType.KIWEN_SELI;   // fire/burning
        case CosmologyZone.HELL_6_PRISON:    return TileType.KIWEN_MOLI;   // death/containment

        // Tartarus — frozen nadir
        case CosmologyZone.TARTARUS:         return TileType.KIWEN_LETE;   // cold/frozen

        // Normal terrain generation (sky, grass surface, stone underground)
        case CosmologyZone.SKY:              return null;
        case CosmologyZone.GRASS_SURFACE:    return null;
        case CosmologyZone.STONE:            return null;

        default: return null;
    }
}

/** Get the underworld layer number (1-6) for hell zones, 7 for tartarus, 0 for non-underworld. */
export function getUnderworldLayer(zone: CosmologyZone): number {
    if (zone >= CosmologyZone.HELL_1_THRESHOLD && zone <= CosmologyZone.HELL_6_PRISON) {
        return zone - CosmologyZone.HELL_1_THRESHOLD + 1;
    }
    if (zone === CosmologyZone.TARTARUS) return 7;
    return 0;
}

// ── World Bounds (in tiles) ────────────────────────────────────────────

/** Tile bounds of the Ouroboros interior (the playable world). */
export const WORLD_BOUNDS = {
    /** Leftmost playable tile X (inside left ouroboros wall). */
    minX: (3 - CENTER_PX) * PIXELS_TO_TILES,                        // pixel 3 → tile -1320
    /** Rightmost playable tile X (inside right ouroboros wall). */
    maxX: (69 - CENTER_PX) * PIXELS_TO_TILES,                       // pixel 69 → tile 1320
    /** Topmost playable tile Y (inside top ouroboros wall). */
    minY: (38 - CENTER_PY) * PIXELS_TO_TILES + TILE_GRID_ORIGIN_Y,  // pixel 38 → tile -787
    /** Bottommost playable tile Y (inside bottom ouroboros wall). */
    maxY: (104 - CENTER_PY) * PIXELS_TO_TILES + TILE_GRID_ORIGIN_Y, // pixel 104 → tile 1853
    /** Tile Y of the grass surface line. */
    surfaceY: 0,
} as const;

// ── Key Landmarks (in tiles) ───────────────────────────────────────────

/** Dungeon tower positions (pixel x, each 1×3 pixels tall at y=55-57). */
export const DUNGEON_TOWER_POSITIONS = [
    { tileX: (10 - CENTER_PX) * PIXELS_TO_TILES, label: 'near Dragon' },
    { tileX: (20 - CENTER_PX) * PIXELS_TO_TILES, label: 'near Phoenix' },
    { tileX: (30 - CENTER_PX) * PIXELS_TO_TILES, label: 'between Phoenix and center' },
    { tileX: (42 - CENTER_PX) * PIXELS_TO_TILES, label: 'between center and Lion' },
    { tileX: (52 - CENTER_PX) * PIXELS_TO_TILES, label: 'near Lion' },
    { tileX: (62 - CENTER_PX) * PIXELS_TO_TILES, label: 'near Turtle' },
] as const;
